import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, ConfirmDialog, ErrorNote, Loading, inputClass } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { trash as trashApi } from '../../lib/resources';
import type { TrashItem } from '../../types';

/** Nome de cada tipo na aba da Lixeira, na ordem em que aparecem. */
const TYPE_LABELS: Array<[string, string]> = [
  ['lead', 'Leads'],
  ['business', 'Negócios'],
  ['activity', 'Atividades'],
  ['attachment', 'Arquivos'],
  ['tag', 'Tags'],
  ['product', 'Produtos'],
  ['loss_reason', 'Motivos de perda'],
  ['list', 'Listas'],
  ['custom_field', 'Campos adicionais'],
  ['custom_field_group', 'Grupos de campos'],
  ['activity_type', 'Tipos de atividade'],
  ['pipeline', 'Pipelines'],
  ['lead_view', 'Visões de leads'],
  ['department', 'Departamentos'],
  ['working_hours', 'Horários de trabalho'],
  ['connection', 'Conexões'],
  ['automation', 'Automações'],
  ['system_parameter', 'Parâmetros'],
];

/** Tudo o que muda depois de restaurar — as telas que mostram o item voltam a buscá-lo. */
const AFFECTED_QUERIES = [
  'tags',
  'lists',
  'products',
  'loss-reasons',
  'activity-types',
  'custom-fields',
  'custom-field-groups',
  'leads',
  'kanban',
  'activities',
  'pipelines',
  'automations',
];

const daysLeft = (purgeAfter: string) =>
  Math.max(0, Math.ceil((Date.parse(purgeAfter) - Date.now()) / 86_400_000));

/** Configurações › Lixeira — restauração em até 30 dias. */
export function TrashSection() {
  const queryClient = useQueryClient();
  const [type, setType] = useState('lead');
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState<{
    item: TrashItem;
    action: 'restore' | 'purge';
  } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const counts = useQuery({ queryKey: ['trash', 'counts'], queryFn: trashApi.counts });
  const list = useQuery({
    queryKey: ['trash', type, search],
    queryFn: () => trashApi.list({ type, search: search || undefined, take: 100 }),
  });

  const settle = (message: string) => {
    setConfirm(null);
    setFeedback(message);
    queryClient.invalidateQueries({ queryKey: ['trash'] });
  };
  const restore = useMutation({
    mutationFn: (item: TrashItem) => trashApi.restore(item.id),
    onSuccess: (item) => {
      AFFECTED_QUERIES.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      settle(`"${item.label}" foi restaurado(a).`);
    },
  });
  const purge = useMutation({
    mutationFn: (item: TrashItem) => trashApi.purge(item.id),
    onSuccess: () => settle('Item excluído definitivamente.'),
  });
  const running = confirm?.action === 'restore' ? restore : purge;

  const retention = counts.data?.retentionDays ?? 30;

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-900">Lixeira</h2>
        <p className="text-xs text-slate-500">
          Itens excluídos ficam aqui por {retention} dias e depois são apagados de vez.
          Restaurar traz de volta o que foi excluído junto (ex.: os negócios e atividades de um
          lead).
        </p>
      </header>

      <div className="flex flex-wrap gap-1 border-b border-slate-100 px-4 py-2" role="tablist">
        {TYPE_LABELS.map(([key, label]) => {
          const count = counts.data?.counts[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={type === key}
              onClick={() => {
                setType(key);
                setFeedback(null);
              }}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                type === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {label}
              {count > 0 && <span className="ml-1.5 text-xs opacity-70">{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 px-5 py-3">
        <input
          className={`${inputClass} mt-0 max-w-xs`}
          placeholder="Pesquisar..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span className="text-xs text-slate-500">{list.data?.count ?? 0} item(ns)</span>
        {feedback && <span className="text-xs text-emerald-600">{feedback}</span>}
      </div>

      {list.isLoading ? (
        <Loading />
      ) : (list.data?.data ?? []).length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-400">Nada na Lixeira.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-5 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">Excluído por</th>
              <th className="px-3 py-2 font-medium">Excluído em</th>
              <th className="px-3 py-2 font-medium">Apagado de vez em</th>
              <th className="px-5 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.data!.data.map((item) => (
              <tr key={item.id}>
                <td className="px-5 py-2.5">
                  <span className="font-medium text-slate-800">{item.label}</span>
                  {item.cascadeCount > 0 && (
                    <span className="block text-xs text-slate-400">
                      + {item.cascadeCount} registro(s) excluído(s) junto
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-500">
                  {item.deletedBy.name ?? 'Sistema'}
                </td>
                <td className="px-3 py-2.5 text-slate-500">{formatDateTime(item.deletedAt)}</td>
                <td className="px-3 py-2.5 text-slate-500">
                  {daysLeft(item.purgeAfter)} dia(s)
                </td>
                <td className="px-5 py-2.5 text-right">
                  <Button
                    className="mr-2"
                    onClick={() => {
                      restore.reset();
                      setConfirm({ item, action: 'restore' });
                    }}
                  >
                    Restaurar
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      purge.reset();
                      setConfirm({ item, action: 'purge' });
                    }}
                  >
                    Excluir de vez
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.action === 'restore' ? 'Restaurar item?' : 'Excluir definitivamente?'}
          message={
            confirm.action === 'restore' ? (
              <p>
                <strong>{confirm.item.label}</strong> volta para onde estava
                {confirm.item.cascadeCount > 0 &&
                  `, junto com ${confirm.item.cascadeCount} registro(s) excluído(s) com ele`}
                .
              </p>
            ) : (
              <p>
                <strong>{confirm.item.label}</strong>
                {confirm.item.cascadeCount > 0 &&
                  ` e ${confirm.item.cascadeCount} registro(s) ligados`}{' '}
                serão apagados do banco. <strong>Não tem volta.</strong>
              </p>
            )
          }
          confirmLabel={confirm.action === 'restore' ? 'Restaurar' : 'Excluir de vez'}
          danger={confirm.action === 'purge'}
          busy={running.isPending}
          error={running.error ? errorMessage(running.error) : null}
          onConfirm={() =>
            confirm.action === 'restore'
              ? restore.mutate(confirm.item)
              : purge.mutate(confirm.item)
          }
          onClose={() => setConfirm(null)}
        />
      )}
      {(restore.error || purge.error) && !confirm && (
        <div className="px-5 pb-4">
          <ErrorNote>{errorMessage(restore.error ?? purge.error)}</ErrorNote>
        </div>
      )}
    </section>
  );
}
